/**
 * ex usage
 * assign id = your id
 * <hh-swiper data-id={your id}>
 *  <div class='swiper swiper--{{your id}}`> // .swiper for styles, .swiper--{{id}} for js
 *    <div class='swiper-wrapper'>
 *      <div class='swiper-slide' </div>
 *    </div>
 *    <div class='swiper-button-prev swiper-button-prev--{{your id}}></div>
 *   <div class='swiper-button-next swiper-button-next--{{your id}}></div>
 *  </div>
 * </hh-swiper>
 *
 *
 * use data-slides-per-view="X" to dictate slides per view
 * use data-speed="X" to dictate transitions speed,
 * use data-loop="true" to enable looping
 * use data-autoplay="true" to enable autoplay, and add a data-autoplay-delay if needed
 * use data-allow-touch-move="false" to disable allowTouchMove, defaults to true
 */

class HHSwiper extends HTMLElement {
  constructor() {
    super()

    this.slidesOffsetAfter =
      this.parseIntAttribute(this.dataset.slidesOffsetAfter) ?? 0
    this.slidesOffsetBefore =
      this.parseIntAttribute(this.dataset.slidesOffsetBefore) ?? 0
    this.spaceBetween = this.parseIntAttribute(this.dataset.spaceBetween) ?? 0
  }

  connectedCallback() {
    const swiperOptions = {
      allowTouchMove: this.dataset.allowTouchMove === 'false' ? false : true,
      autoplay: this.dataset.autoplay
        ? { delay: parseInt(this.dataset.autoplayDelay * 1000) }
        : false,
      enabled: this.dataset.enabled === 'false' ? false : true,
      autoHeight: this.dataset.autoHeight === 'false' ? false : true,
      freeMode: this.dataset.freeMode === 'true' ? true : false,
      mousewheel: {
        forceToAxis: this.dataset.mousewheel === 'true' ? true : false,
      },
      loop: this.dataset.loop,
      slidesOffsetAfter: this.slidesOffsetAfter,
      slidesOffsetBefore: this.slidesOffsetBefore,
      slidesPerView: this.dataset.slidesPerView
        ? this.dataset.slidesPerView
        : 1,
      threshold: parseInt(this.dataset.threshold) || 5,
      spaceBetween: this.spaceBetween,
      speed: this.dataset.speed ? parseInt(this.dataset.speed * 1000) : 300,
      breakpoints: {
        750: {
          slidesOffsetAfter:
            this.parseIntAttribute(this.dataset.slidesOffsetAfterDesktop) ??
            this.slidesOffsetAfter,
          slidesOffsetBefore:
            this.parseIntAttribute(this.dataset.slidesOffsetBeforeDesktop) ??
            this.slidesOffsetBefore,
          spaceBetween:
            this.parseIntAttribute(this.dataset.spaceBetweenDesktop) ??
            this.spaceBetween,
        },
      },
    }

    if (this.dataset.scrollbar) {
      swiperOptions.scrollbar = {
        el: this.dataset.scrollbar,
        draggable: true,
      }
    }

    if (this.dataset.navigation) {
      swiperOptions.navigation = {
        nextEl: this.dataset.nextButton,
        prevEl: this.dataset.prevButton,
      }
    }

    this.swiper = new Swiper(
      this.querySelector(`.swiper--${this.dataset.id}`),
      swiperOptions
    )

    if (this.dataset.hasEmbed) {
      let lastTapTime = 0
      let touchStartTime = 0
      let longPressDuration = 200
      let tapTimeout = null
      let isLongPress = false
      let swipeTriggered = false
      let isDoubleTap = false
      let singleTapTimeout = null

      const swiperContainer = this.querySelector(`.swiper--${this.dataset.id}`)

      swiperContainer.addEventListener('touchstart', function (e) {
        // Detect double tap
        const currentTime = Date.now()
        const tapGap = currentTime - lastTapTime
        lastTapTime = currentTime

        if (tapGap < 300 && tapGap > 0) {
          clearTimeout(tapTimeout) // Cancel long press
          clearTimeout(singleTapTimeout) // Cancel the single tap action
          isDoubleTap = true // Flag to indicate a double tap
          handleDoubleTap(e)
          return
        }

        isDoubleTap = false // Reset flag
        isLongPress = false
        swipeTriggered = false
        touchStartTime = currentTime

        tapTimeout = setTimeout(() => {
          if (Date.now() - touchStartTime >= longPressDuration) {
            isLongPress = true
          }
        }, longPressDuration)
      })

      // On short tap, play or pause video (with delay to differentiate from double tap)
      swiperContainer.addEventListener('touchend', function (e) {
        clearTimeout(tapTimeout) // Clear any long press timeout
        if (!isLongPress && !isDoubleTap) {
          // Check if it's not a long press or double tap
          singleTapTimeout = setTimeout(() => {
            e.target.querySelector('hh-adaptive-video').dispatchEvent(
              new CustomEvent('customVideoEvent', {
                bubbles: true,
                composed: true,
              })
            )
          }, 300) // Delay single tap action by 300ms to differentiate from double tap
        }
        swipeTriggered = false
      })

      // Function to handle double tap on video - Mute or Unmute video
      function handleDoubleTap(e) {
        console.log('Double Tap')
        e.target.querySelector('hh-adaptive-video').dispatchEvent(
          new CustomEvent('customAudioEvent', {
            bubbles: true,
            composed: true,
          })
        )
      }
    }

    this.addEventListener('videoStatus', (e) => this.onVideoPlay(e))
  }

  disconnectedCallback() {
    this.swiper?.destroy()
  }

  onVideoPlay(e) {
    if (!this.dataset.autoplay) return false

    e.detail.playing() === true
      ? this.swiper.autoplay.stop()
      : this.swiper.autoplay.start()
  }

  parseIntAttribute(value) {
    const parsed = Number.parseInt(value)
    if (Number.isNaN(parsed)) {
      return null
    }
    return parsed
  }
}

customElements.define('hh-swiper', HHSwiper)
